'use client'

import { CaretRight } from '@/components/icons/glyphs'
import type { OverviewItem } from '@/lib/items-overview'
import { cn } from '@/lib/utils'
import { useLongPress } from '@/hooks/use-long-press'
import { PhotoThumb } from '@/components/shared/photo-thumb'
import { itemSubtitle } from '@/lib/item-meta'
import { PRESSABLE, WARN_TEXT } from '@/lib/ui-classes'

interface ItemsShelfProps {
  title: string
  count: number
  items: OverviewItem[]
  today: string
  /** Tiles show the room instead of the category (for mixed-room shelves). */
  showRoom?: boolean
  onSelect: (id: string) => void
  /** Long press on a tile. Tiles can't swipe: a sideways drag scrolls the shelf. */
  onLongPress?: (item: OverviewItem) => void
  onSeeAll?: () => void
}

/**
 * A room as a horizontal row of photo tiles. Tiles are 37vw wide so two and a
 * bit fit on any phone: the cut-off third tile is the only scroll affordance.
 * Scrolling is native CSS scroll-snap, so momentum stays off the main thread.
 */
export function ItemsShelf({ title, count, items, today, showRoom, onSelect, onLongPress, onSeeAll }: ItemsShelfProps) {
  return (
    <section aria-label={`${title}, ${count} items`}>
      <div className="flex items-baseline justify-between pb-2.5 pt-6">
        <h2 className="font-serif text-2xl leading-tight">
          {title}
          <span className="ml-1.5 font-sans text-sm font-medium text-muted-foreground tabular-nums">
            {count}
          </span>
        </h2>
        {onSeeAll && (
          <button
            type="button"
            onClick={onSeeAll}
            className="-mr-1 inline-flex items-center gap-0.5 rounded-full px-1 py-1 text-sm font-semibold text-primary"
          >
            See all
            <CaretRight className="size-4" aria-hidden />
          </button>
        )}
      </div>

      <ul
        className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item) => (
          <ShelfTile
            key={item.id}
            item={item}
            subtitle={
              showRoom
                ? { text: item.location?.trim() || 'No room set', warn: false }
                : itemSubtitle(item, today, { showRoom: false, showPurchase: false })
            }
            onSelect={onSelect}
            onLongPress={onLongPress}
          />
        ))}
      </ul>
    </section>
  )
}

function ShelfTile({
  item,
  subtitle,
  onSelect,
  onLongPress,
}: {
  item: OverviewItem
  subtitle: { text: string; warn: boolean }
  onSelect: (id: string) => void
  onLongPress?: (item: OverviewItem) => void
}) {
  const { handlers } = useLongPress(onLongPress ? () => onLongPress(item) : undefined)

  return (
    <li className="w-[37vw] max-w-44 shrink-0 snap-start">
      <button
        type="button"
        onClick={() => onSelect(item.id)}
        {...handlers}
        className={cn('block w-full select-none text-left [-webkit-touch-callout:none]', PRESSABLE)}
        data-testid="item-tile"
      >
        <PhotoThumb src={item.imageUrl} alt="" sizes="40vw" className="aspect-square w-full rounded-2xl" />
        <span className="mt-2 block truncate text-sm font-semibold">{item.name}</span>
        <span
          className={cn(
            'block truncate text-xs first-letter:uppercase',
            subtitle.warn ? WARN_TEXT : 'text-muted-foreground'
          )}
        >
          {subtitle.text || '\u00a0'}
        </span>
      </button>
    </li>
  )
}
